/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { ApiError, apiRequest } from './client';
import type { AuthUser, LoginCredentials } from '../features/auth/types';

export function login(credentials: LoginCredentials): Promise<void> {
  const body = new URLSearchParams({
    username: credentials.username,
    password: credentials.password,
  });
  return apiRequest<void>('/login', { method: 'POST', body });
}

export function logout(): Promise<void> {
  return apiRequest<void>('/logout', { method: 'POST' });
}

export async function getCurrentUser(): Promise<AuthUser> {
  const response = await apiRequest<{ data: string }>('/user');
  return { username: response.data };
}

export type SessionState = 'valid' | 'expired' | 'unknown';

export async function getUserScopes(): Promise<string[]> {
  const response = await apiRequest<{ data?: { userScopes?: string | string[] } }>('/login-user/scopes');
  const scopes = response?.data?.userScopes ?? [];
  return Array.isArray(scopes) ? scopes : scopes.split(/\s+/).filter(Boolean);
}

export async function checkSession(): Promise<SessionState> {
  try {
    await apiRequest<unknown>('/user', { method: 'POST' });
    return 'valid';
  } catch (error) {
    return error instanceof ApiError && error.status === 401 ? 'expired' : 'unknown';
  }
}
